/**
 * Custom hooks for Workflow Configuration (Trigger Types & Available Actions)
 *
 * These hooks fetch configuration data from the backend that powers
 * the workflow editor's Start Node and Action Node property panels.
 */
import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import { apiGet } from "@/lib/api-client";
import type {
    TriggerTypeResponse,
    WorkflowActionResponse,
} from "@/types/api/workflows";

const WORKFLOW_CONFIG_QUERY_KEYS = {
    triggerTypes: ["workflows", "trigger-types"] as const,
    availableActions: ["workflows", "available-actions"] as const,
    availableActionsFiltered: (category?: string, includeDangerous?: boolean) =>
        [...WORKFLOW_CONFIG_QUERY_KEYS.availableActions, { category, includeDangerous }] as const,
};

/**
 * Hook per ottenere i tipi di trigger disponibili per i workflow
 *
 * Usato nello Start Node per mostrare il dropdown dei trigger selezionabili.
 * Ogni trigger type include:
 * - event_type: Codice del tipo evento (es. "status_changed")
 * - label: Nome leggibile in italiano
 * - description: Descrizione dell'evento
 * - requires_conditions: Se richiede condizioni obbligatorie
 * - condition_fields: Schema dei campi condizione disponibili
 */
export function useTriggerTypes(
    options?: Omit<UseQueryOptions<TriggerTypeResponse[], Error>, "queryKey" | "queryFn">
) {
    return useQuery({
        queryKey: WORKFLOW_CONFIG_QUERY_KEYS.triggerTypes,
        queryFn: async (): Promise<TriggerTypeResponse[]> => {
            return await apiGet<TriggerTypeResponse[]>("/workflows/trigger-types");
        },
        staleTime: 1000 * 60 * 30, // 30 minutes - trigger types rarely change
        ...options,
    });
}

/**
 * Hook per ottenere le azioni disponibili per i nodi Action
 *
 * Usato nell'Action Node per mostrare la lista di azioni selezionabili.
 * Include sia le azioni native del workflow che i tool registrati nel database.
 *
 * @param category - Filtra per categoria (es. "notification", "document")
 * @param includeDangerous - Includi azioni pericolose (default: true)
 */
export function useWorkflowActions(
    category?: string,
    includeDangerous: boolean = true,
    options?: Omit<UseQueryOptions<WorkflowActionResponse[], Error>, "queryKey" | "queryFn">
) {
    return useQuery({
        queryKey: WORKFLOW_CONFIG_QUERY_KEYS.availableActionsFiltered(category, includeDangerous),
        queryFn: async (): Promise<WorkflowActionResponse[]> => {
            const params: Record<string, string | boolean> = {};
            if (category) params.category = category;
            params.include_dangerous = includeDangerous;

            return await apiGet<WorkflowActionResponse[]>("/workflows/available-actions", { params });
        },
        staleTime: 1000 * 60 * 15, // 15 minutes - actions change when tools are updated
        ...options,
    });
}

/**
 * Hook per ottenere tutte le azioni disponibili raggruppate per categoria
 *
 * Utile per mostrare un picker categorizzato delle azioni
 */
export function useWorkflowActionsGrouped(
    includeDangerous: boolean = true,
    options?: Omit<UseQueryOptions<Map<string, WorkflowActionResponse[]>, Error>, "queryKey" | "queryFn">
) {
    return useQuery({
        queryKey: [...WORKFLOW_CONFIG_QUERY_KEYS.availableActions, "grouped", { includeDangerous }] as const,
        queryFn: async (): Promise<Map<string, WorkflowActionResponse[]>> => {
            const params: Record<string, boolean> = {
                include_dangerous: includeDangerous,
            };

            const actions = await apiGet<WorkflowActionResponse[]>("/workflows/available-actions", { params });

            // Group by category
            const grouped = new Map<string, WorkflowActionResponse[]>();
            for (const action of actions) {
                const category = action.category_label || action.category;
                if (!grouped.has(category)) {
                    grouped.set(category, []);
                }
                grouped.get(category)!.push(action);
            }

            return grouped;
        },
        staleTime: 1000 * 60 * 15,
        ...options,
    });
}

// Export query keys for external use (e.g., invalidation)
export { WORKFLOW_CONFIG_QUERY_KEYS };
